import { useTranslation } from 'react-i18next';
import FullCalendar from '@fullcalendar/react';
import { useNavigate } from 'react-router-dom';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import itLocale from '@fullcalendar/core/locales/it';
import arLocale from '@fullcalendar/core/locales/ar';
import multiMonthPlugin from '@fullcalendar/multimonth';
import enLocale from '@fullcalendar/core/locales/en-gb';
import interactionPlugin from '@fullcalendar/interaction';
import React, { useMemo, useState, useEffect, useCallback } from 'react';

import { Box, Select, Tooltip, MenuItem, Container, Typography } from '@mui/material';

import { genericApiToaster } from 'src/services/utils';

import { useAuth } from '../../hooks/useAuth';
import { encrypt } from '../../utils/hashCode';
import { convertLocalTime } from '../../utils/convertTime';
import { fetchStatuses } from '../../services/status.service';
import { getUsersByDepartments } from '../../services/user.service';
import {
  fetchAgentTickets,
  fetchTicketsByDepartments,
} from '../../services/ticket.service';

const localLanguage = { it: itLocale, en: enLocale, ar: arLocale };
const colors = [
  '#3d91ff',
  '#3ecf8e',
  '#fed14c',
  '#9c27b0',
  '#aa2e25',
  '#33eaff',
  '#827717',
];

export default function AssignmentsCalendar() {
  const [tickets, setTickets] = useState([]);
  const [agent, setAgent] = useState('');
  const [selectedAgentId, setSelectedAgentId] = useState(0);
  const [options, setOptions] = useState([]);
  const [agentsList, setAgentsList] = useState([]);
  const [statusesList, setStatusesList] = useState([]);
  const { t, i18n } = useTranslation();

  const workdays = useMemo(() => [1, 2, 3, 4, 5], []);
  const workHours = useMemo(() => [8, 17], []);
  const workMinutes = useMemo(() => ['00', '00'], []);

  const slotMin = useMemo(
    () => `${workHours[0] - 2}:${workMinutes[0]}`,
    [workHours, workMinutes]
  );
  const slotMax = useMemo(
    () => `${workHours[1] + 2}:${workMinutes[1]}`,
    [workHours, workMinutes]
  );

  const { role, id: currentUserId, departmentIds } = useAuth();
  const navigate = useNavigate();

  const currentLocale = useMemo(
    () => (i18n.language ? localLanguage[i18n.language] : arLocale),
    [i18n.language]
  );

  const getStatuses = useCallback(
    (ids) => {
      if (!ids?.length) return;
      genericApiToaster(fetchStatuses, [], '', '', null, null, t, (res) =>
        setStatusesList(
          res.filter((s) => ids.includes(s.departmentId) || s.departmentId === 0)
        )
      );
    },
    [t]
  );

  const fetchTickets = useCallback(() => {
    let apiCall;
    let apiArg;
    if (role === 'ROLE_TICKETMANAGER') {
      apiCall = fetchAgentTickets;
      apiArg = currentUserId;
    } else if (role === 'ROLE_DEPARTMENTMANAGER') {
      apiCall = selectedAgentId === 0 ? fetchTicketsByDepartments : fetchAgentTickets;
      apiArg = selectedAgentId === 0 ? departmentIds : selectedAgentId;
    }
    if (!apiCall || apiArg === undefined) return;

    const finalArgs = apiCall === fetchTicketsByDepartments ? [apiArg] : [apiArg];

    genericApiToaster(apiCall, finalArgs, '', '', null, null, t, (res) => {
      const mapped = res
        .map((tk) => ({
          ...tk,
          statusObject: statusesList.find((s) => s.statusId === tk.status_id) || {
            name: 'Unknown',
          },
        }))
        .filter(
          (tk) =>
            tk.statusObject?.systemMappedStatus === 'RESOLVED' ||
            tk.statusObject?.systemMappedStatus === 'OPEN'
        );
      setTickets(mapped);
    });
  }, [role, currentUserId, departmentIds, selectedAgentId, statusesList, t]);

  const fetchAgents = useCallback(() => {
    if (!departmentIds?.length) return;

    genericApiToaster(
      getUsersByDepartments,
      [departmentIds],
      '',
      '',
      null,
      null,
      t,
      (res) => setAgentsList(res.filter((u) => u.role === 'ROLE_TICKETMANAGER'))
    );
  }, [departmentIds, t]);

  useEffect(() => {
    if (departmentIds?.length > 0) {
      fetchAgents();
      getStatuses(departmentIds);
    }
  }, [departmentIds, fetchAgents, getStatuses]);

  useEffect(() => {
    if (role && statusesList.length > 0) {
      fetchTickets();
    }
  }, [selectedAgentId, statusesList, role, fetchTickets]);

  useEffect(() => {
    const opt = [
      { id: 0, name: 'All ticket managers' },
      ...agentsList.map((ag) => ({ id: ag.id, name: ag.username })),
    ];
    setOptions(opt);
    if (opt.length > 0) {
      const current = opt.find((o) => o.id === selectedAgentId);
      setAgent(current?.name || opt[0].name);
    }
  }, [agentsList, selectedAgentId]);

  const splitEventByWorkdays = useCallback(
    (event) => {
      const [startH, endH] = workHours;
      const parts = [];
      const curr = new Date(event.start);
      const end = new Date(event.end);
      while (curr <= end) {
        if (workdays.includes(curr.getDay())) {
          const isFirst = curr.getTime() === new Date(event.start).getTime();
          const isLast = curr.toDateString() === end.toDateString();
          const pStart = new Date(curr);
          pStart.setHours(isFirst ? Math.max(curr.getHours(), startH) : startH);
          const pEnd = new Date(curr);
          pEnd.setHours(isLast ? end.getHours() : endH);
          parts.push({ ...event, start: pStart, end: pEnd, ticketId: event.id });
        }
        curr.setDate(curr.getDate() + 1);
        curr.setHours(startH, 0, 0, 0);
      }
      return parts;
    },
    [workdays, workHours]
  );

  const events = useMemo(
    () =>
      tickets.map((tk, idx) => ({
        start: tk.estimatedStartTime
          ? convertLocalTime(new Date(tk.estimatedStartTime))
          : convertLocalTime(new Date(tk.acceptedAt)),
        end:
          tk.statusObject?.systemMappedStatus === 'RESOLVED' && tk.resolvedAt
            ? convertLocalTime(new Date(tk.resolvedAt))
            : tk.estimatedEndTime
            ? convertLocalTime(new Date(tk.estimatedEndTime))
            : convertLocalTime(new Date()),
        title: tk.title,
        agentName: tk.agentName,
        color: colors[idx % colors.length],
        id: tk.id,
        statusObject: tk.statusObject,
        createdBy: tk.customerName,
      })),
    [tickets]
  );

  const flattenedEvents = useMemo(
    () => events.flatMap(splitEventByWorkdays),
    [events, splitEventByWorkdays]
  );

  const handleChange = (event) => {
    setAgent(event.target.value);
    const found = options.find((o) => o.name === event.target.value);
    if (found) setSelectedAgentId(found.id);
  };

  return (
    <Container>
      {role === 'ROLE_DEPARTMENTMANAGER' && (
        <Select
          value={agent}
          onChange={handleChange}
          sx={{ maxWidth: '25vh', minWidth: '20vh', mx: 6, my: 1 }}
        >
          {options.map((o) => (
            <MenuItem key={o.id} value={o.name}>
              {o.name}
            </MenuItem>
          ))}
        </Select>
      )}
      <FullCalendar
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, multiMonthPlugin]}
        initialView="dayGridMonth"
        height="75vh"
        locale={currentLocale}
        headerToolbar={{
          start: 'prev',
          center: 'title',
          end: 'next multiMonthYear,dayGridMonth,timeGridWeek,timeGridDay',
        }}
        events={flattenedEvents}
        businessHours={{
          daysOfWeek: workdays,
          startTime: `${workHours[0]}:${workMinutes[0]}`,
          endTime: `${workHours[1]}:${workMinutes[1]}`,
        }}
        eventDisplay="block"
        allDaySlot={false}
        slotMinTime={slotMin}
        slotMaxTime={slotMax}
        nowIndicator
        eventContent={(arg) => {
          const { statusObject, ticketId, agentName } = arg.event.extendedProps;
          const sName =
            statusObject?.statusId < 6
              ? t(`status.${statusObject.name}`)
              : statusObject?.name;
          return (
            <Tooltip
              title={
                <div>
                  <b>ID:</b> {ticketId}
                  <br />
                  <b>Status:</b> {sName}
                </div>
              }
            >
              <Box
                onClick={() => navigate(`/tickets/${encrypt(ticketId)}`)}
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                <Typography variant="caption">
                  <b>{arg.timeText}</b>{' '}
                  {role === 'ROLE_DEPARTMENTMANAGER' ? `- ${agentName}` : ''} -{' '}
                  {arg.event.title}
                </Typography>
              </Box>
            </Tooltip>
          );
        }}
      />
    </Container>
  );
}
